import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import filter, { resolveFilterLabelsToIds } from '../../versa/filter.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import record, { flatten, deepen } from '../../versa/record.js';
import { notify, ask } from '../../versa/msgbox.js';
import { splitIdsByCompany } from '../../versa/misc.js';
import pdf from '../../versa/pdf.js';
import view from '../views/inputs.js';
import form from '../forms/inputs.js';
import inventory_form from '../forms/inputs-inventory.js';
import putaway_form from '../forms/inputs-putaway.js';
import create_invoices_form from '../forms/create-invoices.js';
import print_labels_form from '../forms/inputs-labels.js';
import reverse_form from '../forms/inputs-reverse.js';
import * as types from '../types.js';

const inputs = function()
{

	let inputs_crud = crud.create({
		async loadBaseData()
		{
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch(error => notify(error)));
			api.call(api.list_warehouses, this.queryAll()).then((data) => db.set('warehouses', data).catch(error => notify(error)));
			api.call(api.list_locations, await this.queryFilteredForLocations()).then((data) => db.set('locations', data).catch(error => notify(error)));
			api.call(api.list_programs, this.queryAll()).then((data) => db.set('programs', data).catch(() => {}));
			api.call(api.list_products, this.queryAll()).then((data) => db.set('products', data).catch(error => notify(error)));
			api.call(api.list_units, this.queryAll()).then((data) => db.set('units', data).catch(error => notify(error)));
			api.call(api.list_lots, this.queryAll()).then((data) => db.set('lots', data).catch(error => notify(error)));
			api.call(api.list_devices, this.queryAll()).then((data) => db.set('devices', data).catch(error => notify(error)));
		},
		async queryData(query)
		{
			return await api.call(api.list_inputs, query);
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_input, ...JSON.parse(id));
			return flatten(_record, 'items');
		},
		async editFilter() {
			api.call(api.list_locations, await this.queryFilteredForLocations()).then((data) => db.set('locations', data)).catch(() => {});
			filter.edit(this.applyFilter.bind(this), this.filter, {
				reloadLocations: async (formData) => {
					const resolved = await resolveFilterLabelsToIds(formData, this.fieldspecs);
					const parts = [];
					for (let i = 0; i < 10; i++) {
						const f = resolved[`field-${i}`];
						const v = resolved[`value-${i}`];
						if (f === 'company_id' && v) parts.push(`company_id*eq*${v}`);
						else if (f === 'warehouse_id' && v != null && v !== '') parts.push(`warehouse_id*eq*${v}`);
						else if (f === 'warehouse_company_id' && v) parts.push(`company_id*eq*${v}`);
					}
					if (parts.length === 0) return;
					const params = new URLSearchParams();
					params.append('filter', parts.join(','));
					params.append('sort', '4');
					params.append('limit', '0');
					const data = await api.call(api.list_locations, '?' + params.toString());
					if (data && Array.isArray(data)) db.set('locations', data);
				}
			});
		},
		async applyFilter(data) {
			this.filter = await resolveFilterLabelsToIds(data, this.fieldspecs);
			api.call(api.list_locations, await this.queryFilteredForLocations()).then((data) => db.set('locations', data)).catch(() => {});
			this.loadPage(1);
		},
		async saveRecord(data, old_data)
		{
			data = deepen(data, 'items');
			let _record = {
				order_type: data.order_type,
				order_id: Number(data.order_id),
				warehouse_company_id: data.warehouse_company_id,
				warehouse_id: Number(data.warehouse_id),
				location_id: Number(data.location_id),
				program_id: (data.program_id != null && data.program_id !== '' && Number(data.program_id) >= 0) ? Number(data.program_id) : null,
				invoice_number: data.invoice_number,
				invoice_xml: data.invoice_xml,
				volumes: Number(data.volumes),
				weight: Number(data.weight),
				status: Number(data.status),
				items: data.items
				.filter(item => item && (item.product_id != null && item.product_id !== '') && Number(item.product_id) > 0)
				.map(item => ({ 
					product_id: Number(item.product_id),
					unit_id: Number(item.unit_id),
					quantity: Number(item.quantity),
					unit_price: Number(item.unit_price)
				}))
			};
			if (old_data)
				return await api.call(api.update_input, data.company_id, data.id, _record);
			else
				return await api.call(api.insert_input, data.company_id, _record);
		},
		async inventory()
		{
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'lots');
				let _record = {
					warehouse_company_id: data.warehouse_company_id,
					warehouse_id: Number(data.warehouse_id),
					location_id: Number(data.location_id),
					program_id: (data.program_id != null && data.program_id !== '') ? Number(data.program_id) : null,
					lots: data.lots.map(lot => ({ 
						id: Number(lot.id),
						product_id: Number(lot.product_id),
						lot_number: lot.lot_number,
						manufacture_date: lot.manufacture_date,
						expiration_date: lot.expiration_date,
						quantity: Number(lot.quantity),
						status: Number(lot.status),
						is_blocked: lot.is_blocked == '1'	// Can be: undefined, '0', '1'
					}))
				};
				if (await api.call(api.inventory_input, data.company_id, data.input_id, _record))
				{
					api.call(api.list_lots, this.queryAll()).then((data) => db.set('lots', data).catch(error => notify(error)));	// Get new lots
					this.loadPage();	// Show updated input status
					return true;
				}
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para conferência', 'Conferência de Entrada');
			let input = await api.call(api.select_input, ids[0][0], ids[0][1]);
			let lots = await api.call(api.list_lots, `?filter=company_id*eq*${input.company_id},input_id*eq*${input.id}&limit=0`);
			let _record = {
				'company_id': input.company_id,
				'input_id': input.id,
				'warehouse_company_id': input.warehouse_company_id,
				'warehouse_id': input.warehouse_id,
				'location_id': input.location_id,
				'program_id': input.program_id ?? '',
				'lots': lots.map(lot => { lot.is_blocked = Boolean(lot.status & 0b1000); return lot; })
			};
			await api.call(api.list_programs, this.queryAll()).then((data) => db.set('programs', data).catch(() => {}));
			record.edit(inventory_form, sendRequest, flatten(_record, 'lots'));
		},
		async putAway()
		{
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'puts');
				let _record = {
					'puts': data.puts.map(put => ({ 
						//stock_id: Number(put.id),
						lot_id: Number(put.lot_id),
						location_id: Number(put.location_id),
						quantity: Number(put.quantity)
					}))
				};
				if (await api.call(api.putaway_input, data.company_id, data.input_id, _record))
				{
					this.loadPage();	// Show updated input status
					return true;
				}
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para endereçamento', 'Endereçamento de Lotes');
			let input = await api.call(api.select_input, ids[0][0], ids[0][1]);
			let stocks = await api.call(api.list_stocks_by_input, input.company_id, input.id);
			stocks = stocks.filter(stock => stock.location_id != input.location_id);	// Only show stocks that didn't come from the staging position
			stocks.map(stock => { stock.quantity = stock.quantity_available; return stock; });
			let _record = {
				'company_id': input.company_id,
				'input_id': input.id,
				'warehouse_company_id': input.warehouse_company_id,
				'warehouse_id': input.warehouse_id,
				'stocks': stocks
			};
			let fs = record.edit(putaway_form, sendRequest, flatten(_record, 'stocks'));
		},
		printInputs()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_inputs, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório?`, 'Listagem de Entradas', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printInputSKUs()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_input_skus, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas na listagem?`, 'Listagem de Nota Fiscal', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printXML()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_input_xml, { 'ids': ids }).then(showResult);
			}
			ask(`Gerar espelho da nota de ${this.getCount()} entradas?`, 'Espelho da Nota Fiscal', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printPutAwayOrder()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_putaway, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório?`, 'Ordem de Endereçamento', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		async printLabels()
		{
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'lots');
				let _record = {
					device_company_id: data.device_company_id,
					device_id: Number(data.device_id),
					return_zpl: !!data.use_local_agent,
					lots: data.lots.map(lot => ({ 
						lot_id: Number(lot.id),
						labels: Number(lot.labels)
					}))
				};
				const result = await api.call(api.print_input_labels, data.company_id, data.input_id, _record);
				if (result?.zpl) {
					try {
						await api.sendZplToAgent(result.zpl, result.printer, result.printer_key);
						notify('Etiquetas enviadas ao agente local.', 'Impressão');
					} catch (e) {
						notify(e?.message || String(e), 'Agente local');
						throw e;
					}
				}
				return result;
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para impressão', 'Etiquetas de Lotes');
			let input = await api.call(api.select_input, ids[0][0], ids[0][1]);
			let lots = await api.call(api.list_lots, `?filter=company_id*eq*${input.company_id},input_id*eq*${input.id}&limit=0`);
			let _record = {
				'company_id': input.company_id,
				'input_id': input.id,
				'lots': lots.map(lot => { lot.labels = 1; return lot; })
			};
			record.edit(print_labels_form, sendRequest, flatten(_record, 'lots'));
		},
		printCosts()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_input_costs, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório?`, 'Relatório de Custos', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printConference()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_input_conference, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório de conferência?`, 'Relatório de Conferência', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printDetails()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_input_details, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório detalhado?`, 'Entrada Detalhada', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		createInvoices()
		{
			const showResult = (resp) =>
			{
				this.clearGridSelection();
				notify(`Notas criadas: ${resp?.count || 0}`, 'Resultado');
				fs.destroy();
			}
			const sendRequest = async (data) =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.create_invoices, Object.assign(data, { 'io_type': 'I', 'ids': ids })).then(showResult);
			}
			let fs = formset.create(create_invoices_form, sendRequest);
			fs.init = (() =>
			{
				document.querySelector(`${fs.form.selector} #count`).innerHTML = this.getCount();
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
		async reverseInput()
		{
			const sendRequest = async (data) =>
			{
				let resp = await api.call(api.reverse_input, data.company_id, data.input_id, { status: Number(data.status) });
				if (resp)
					this.loadPage();	// Show updated status
				return resp;
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para estornar', 'Estorno de Entrada');
			record.edit(reverse_form, sendRequest, { company_id: ids[0][0], input_id: ids[0][1] });
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Entrada', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador da entrada' },
		{ index: 4, field: 'order_id', label: 'Pedido', types: [types.number(0, 2)], placeholder: '0', title: 'Número do pedido vinculado' },
		{ index: 3, field: 'order_type', label: 'Origem', types: [types.opt_order_type()], placeholder: '', title: 'Origem do pedido vinculado' },
		{ index: 10, field: 'invoice_number', label: 'Nota', types: [], placeholder: '', title: 'Número da nota fiscal' },
		{ index: 11, field: 'invoice_xml', label: 'XML', types: [], placeholder: '', title: 'XML da nota fiscal' },
		{ index: 5, field: 'created_on', label: 'Criação', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora da criação da entrada' },
		{ index: 6, field: 'received_on', label: 'Recebimento', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora de recebimento da mercadoria' },
		{ index: 7, field: 'warehouse_company_id', label: 'Destinatária', types: [types.datalist()], placeholder: '', title: 'Empresa do armazém de recebimento', store: 'companies', labels: ['nick'] },
		{ index: 8, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém de recebimento', store: 'warehouses', labels: ['name'] },
		{ index: 15, field: 'program_id', label: 'Programa', types: [types.datalist()], placeholder: '', title: 'Programa da entrada', store: 'programs', labels: ['name'] },
		{ index: 9, field: 'location_id', label: 'Posição', types: [types.datalist()], placeholder: '', title: 'Posição no armazém', store: 'locations', labels: ['address'], separator: '-' },
		{ index: 12, field: 'volumes', label: 'Volumes', types: [types.number(0, 2)], placeholder: '0', title: 'Número de volumes' },
		{ index: 13, field: 'weight', label: 'Peso', types: [types.number(3)], placeholder: '0,000', title: 'Peso total dos volumes' },
		{ index: 14, field: 'status', label: 'Status', types: [types.opt_input_status(), types.datalist()], placeholder: '', title: 'Status da entrada' },
	];

	let operations = [
		{ hash: '#inventory-input',		label: 'Conferência&nbsp;de&nbsp;Entrada' 	},
		{ hash: '#submit-putaway',		label: 'Endereçamento&nbsp;de&nbsp;Lotes' 	},
		{ hash: '#create-invoices',		label: 'Gerar&nbsp;Notas&nbsp;Fiscais'		},
		{ hash: '#reverse-input',		label: 'Estornar&nbsp;Entrada'				},
	];

	let reports = [
		{ hash: '#print-inputs',		label: 'Listagem&nbsp;de&nbsp;Entradas' 			},
		{ hash: '#print-skus',			label: 'Cruzamento&nbsp;de&nbsp;Nota&nbsp;Fiscal'	},
		{ hash: '#print-xml', 			label: 'Espelho&nbsp;da&nbsp;Nota&nbsp;Fiscal' 		},
		{ hash: '#print-putaway', 		label: 'Ordem&nbsp;de&nbsp;Endereçamento' 			},
		{ hash: '#print-labels',		label: 'Etiquetas&nbsp;de&nbsp;Lotes' 				},
		{ hash: '#print-costs',			label: 'Relatório&nbsp;de&nbsp;Custos' 				},
		{ hash: '#print-conference',	label: 'Relatório&nbsp;de&nbsp;Conferência' 		},
		{ hash: '#print-details',		label: 'Entrada&nbsp;Detalhada' 					},
	];

	router.set('#inventory-input',		inputs_crud.inventory.bind(inputs_crud));
	router.set('#submit-putaway', 		inputs_crud.putAway.bind(inputs_crud));
	router.set('#create-invoices', 		inputs_crud.createInvoices.bind(inputs_crud));
	router.set('#reverse-input', 		inputs_crud.reverseInput.bind(inputs_crud));

	router.set('#print-inputs', 		inputs_crud.printInputs.bind(inputs_crud));
	router.set('#print-skus', 			inputs_crud.printInputSKUs.bind(inputs_crud));
	router.set('#print-xml', 			inputs_crud.printXML.bind(inputs_crud));
	router.set('#print-putaway', 		inputs_crud.printPutAwayOrder.bind(inputs_crud));
	router.set('#print-labels', 		inputs_crud.printLabels.bind(inputs_crud));
	router.set('#print-costs', 			inputs_crud.printCosts.bind(inputs_crud));
	router.set('#print-conference', 	inputs_crud.printConference.bind(inputs_crud));
	router.set('#print-details', 		inputs_crud.printDetails.bind(inputs_crud));

	inputs_crud.run(
		'inputs',
		'Entradas',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports
	);

};

export default inputs;

